import React, { useState, useRef, useEffect, useMemo } from "react";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { Book } from "@/types/book";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useReviews } from "@/hooks/use-reviews";
import { CloseButton } from "@/components/book-dialog/CloseButton";
import { IndividualBookPurchase } from "@/components/IndividualBookPurchase";
import { useSmartBookAvailability } from "@/hooks/use-smart-book-availability";
import { StarRating } from "@/components/reviews/StarRating";
import { CommentButton } from "@/components/book-dialog/CommentButton";
import { CommentsBottomSheet } from "@/components/book-dialog/CommentsBottomSheet";
import { Clock, BookmarkPlus, BookmarkMinus, Volume2, Play, Plus, Check, Share2 } from "lucide-react";
import { PiShareFatFill } from "react-icons/pi";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/components/AuthProvider";
import { useAuthModal } from "@/hooks/use-auth-modal";
import { AuthModal } from "@/components/AuthModal";
import { useRandomBooks } from "@/hooks/use-random-books";
import { useEnhancedReadingList } from "@/hooks/use-enhanced-reading-list";
import { useAppConfig } from "@/hooks/use-app-config";
import { useSubscriptionStatus } from "@/hooks/use-subscription-status";
import { useBookData } from "@/hooks/use-book-data";
import { SubscriptionPurchaseButton } from "@/components/SubscriptionPurchaseButton";
import { getAgeRating } from "@/lib/age-rating";
import { BookCastRow } from "@/components/book-dialog/BookCastRow";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSeriesEpisodes } from "@/hooks/use-series-episodes";

interface BookDialogProps {
  book: Book | null;
  isOpen: boolean;
  onClose: () => void;
  onReadNow: () => void;
  onPurchase: () => void;
  hasAccess: boolean;
  bundlePrice?: number;
  bundleId?: number;
  earlyAccessBookId?: number | null;
  isEarlyAccess?: boolean;
  onBookClick?: (book: Book) => void;
  allBooks?: Book[];
}

export function BookDialog({
  book,
  isOpen,
  onClose,
  onReadNow,
  onPurchase,
  hasAccess,
  bundlePrice,
  bundleId,
  earlyAccessBookId,
  isEarlyAccess,
  onBookClick,
  allBooks
}: BookDialogProps) {
  const isMobile = useIsMobile();
  const [showIndividualPurchase, setShowIndividualPurchase] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const { session } = useAuth();
  const { isAuthModalOpen, authContext, openAuthModal, closeAuthModal } = useAuthModal();

  const { data: appConfig } = useAppConfig();
  const { data: subscriptionStatus } = useSubscriptionStatus();
  const {
    isBookInReadingList,
    addToReadingList,
    removeFromReadingList,
    isAdding,
    isRemoving
  } = useEnhancedReadingList();

  const needsPurchaseOptions = !hasAccess && session?.user?.id;
  const { data: availability, isLoading: availabilityLoading } = useSmartBookAvailability(needsPurchaseOptions ? book : null);

  const relatedBooks = useRandomBooks(allBooks || [], book?.id ? Number(book.id) : undefined, 5);
  
  // Series/episode detection
  const seriesInfo = useSeriesEpisodes(book);

  const bookId = book?.id ? Number(book.id) : null;
  const bookDataQuery = useBookData(bookId || 0);

  // Fetch page_images for random hero image
  const { data: pageImagesData } = useQuery({
    queryKey: ['book-page-images', bookId],
    queryFn: async () => {
      if (!bookId) return null;
      const { data, error } = await supabase
        .from('books')
        .select('page_images')
        .eq('id', bookId)
        .single();
      
      if (error) {
        console.error('[BookDialog] Error fetching page_images:', error);
        return null;
      }
      
      // Handle various formats - could be array, JSON string, or null
      let images = data?.page_images;
      if (typeof images === 'string') {
        try {
          images = JSON.parse(images);
        } catch {
          return null;
        }
      }
      
      return Array.isArray(images) ? images as Array<{ pageName: string; url: string }> : null;
    },
    enabled: !!bookId,
    staleTime: 5 * 60 * 1000,
  });

  // Select a random page image (excluding cover), stable per book - shown to all users as a teaser
  const randomPageImage = useMemo(() => {
    if (!pageImagesData || pageImagesData.length === 0) return null;
    // Filter out cover images
    const storyPages = pageImagesData.filter(img => 
      img && img.pageName && !img.pageName.toLowerCase().includes('cover')
    );
    if (storyPages.length === 0) return null;
    // Use book ID as seed for stable random selection
    const randomIndex = bookId ? (bookId % storyPages.length) : 0;
    const selectedImage = storyPages[randomIndex];
    return selectedImage?.url || null;
  }, [pageImagesData, bookId]);

  const { reviews, isLoading, totalCount } = bookId ? useReviews(bookId, true, true) : {
    reviews: undefined,
    isLoading: false,
    totalCount: 0
  };

  const averageRating = reviews?.length 
    ? (reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length).toFixed(1) 
    : book?.rating?.toFixed(1) || "0.0";

  const reviewCount = totalCount || 0;
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [internalOpen, setInternalOpen] = useState(isOpen);
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [displayedBookId, setDisplayedBookId] = useState(book?.id);
  const [pageImageLoaded, setPageImageLoaded] = useState(false);
  
  // Reset page image loaded state when book changes
  useEffect(() => {
    setPageImageLoaded(false);
  }, [bookId]);
  
  useEffect(() => {
    if (isOpen) setInternalOpen(true);
  }, [isOpen]);

  useEffect(() => {
    if (book?.id && book.id !== displayedBookId) {
      setIsTransitioning(true);
      const timer = setTimeout(() => {
        setDisplayedBookId(book.id);
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTo({ top: 0, behavior: 'instant' });
        }
        setTimeout(() => setIsTransitioning(false), 50);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [book?.id, displayedBookId]);

  const handleRequestClose = React.useCallback(() => {
    setInternalOpen(false);
    window.setTimeout(() => {
      onClose();
    }, 350);
  }, [onClose]);

  if (!book) return null;

  const aboutText = bookDataQuery.data?.short_description?.trim() || book.short_description?.trim() || null;
  const ageRating = getAgeRating(book.tags, book.bundle?.title);

  const calculateReadingTime = () => {
    const pageCountCandidates = [
      book.compiled_book?.metadata?.totalPages,
      Array.isArray(book.compiled_book?.pages) ? book.compiled_book?.pages?.length : undefined,
    ];
    const totalPages = pageCountCandidates.find(v => typeof v === 'number' && v > 0) as number | undefined ?? 15;
    if (totalPages < 60) return `${totalPages} min`;
    const hours = Math.floor(totalPages / 60);
    const minutes = totalPages % 60;
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  };

  const readingTime = calculateReadingTime();
  
  const getBookYear = () => {
    if (book.year) return String(book.year);
    if (book.created_at) return String(new Date(book.created_at).getFullYear());
    return new Date().getFullYear().toString();
  };
  const bookYear = getBookYear();

  const getCacheBustedCoverUrl = (url: string | null) => {
    if (!url) return '';
    try {
      const base = url.startsWith('http') ? undefined : window.location.origin;
      const imageUrl = new URL(url, base);
      if (book.cache_version != null) imageUrl.searchParams.set('v', String(book.cache_version));
      return imageUrl.toString();
    } catch {
      return url || '';
    }
  };

  const hasAudio = book.compiled_book?.audioEnabled || 
    (book.compiled_book?.audio?.pageAudioUrls && Object.keys(book.compiled_book.audio.pageAudioUrls).length > 0);

  const isUpcoming = book.bundle?.status === 'upcoming' || book.bundle?.status === 'building';

  const handleReadingListToggle = () => {
    if (book && isBookInReadingList(book.id)) {
      removeFromReadingList(book.id);
    } else if (book) {
      addToReadingList(book.id);
    }
  };

  // Shared button components - Apple TV style
  const ReadNowButton = ({ className = "" }: { className?: string }) => (
    <Button 
      onClick={() => {
        if (!isUpcoming) {
          console.log('Read Now clicked - navigating to book:', book.id);
          onReadNow();
        }
      }} 
      disabled={isUpcoming}
      className={cn(
        "h-11 flex-1 bg-white text-black hover:bg-white/90 font-semibold rounded-xl text-base transition-all gap-1",
        className
      )}
    >
      <Play className="w-4 h-4 fill-current" />
      {isUpcoming ? 'Coming Soon' : 'Play'}
    </Button>
  );

  const AddToListButton = ({ iconOnly = false, className = "" }: { iconOnly?: boolean; className?: string }) => (
    <Button
      onClick={handleReadingListToggle}
      disabled={isAdding || isRemoving}
      variant="ghost"
      className={cn(
        "h-11 transition-all disabled:opacity-50 rounded-xl bg-white/10 backdrop-blur-sm text-white border border-white/20 hover:bg-white/20 gap-1",
        iconOnly ? "w-11 px-0" : "flex-1",
        className
      )}
    >
      {isAdding || isRemoving ? (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
      ) : isBookInReadingList(book.id) ? (
        <>
          <Check className="w-4 h-4 text-white" />
          {!iconOnly && <span className="font-medium text-base">My List</span>}
        </>
      ) : (
        <>
          <Plus className="w-4 h-4 text-white" />
          {!iconOnly && <span className="font-medium text-base">My List</span>}
        </>
      )}
    </Button>
  );
  // Quality badges component
  const QualityBadges = ({ className = "" }: { className?: string }) => (
    <div className={cn("flex flex-wrap items-center gap-1.5 text-white/50 text-[11px]", className)}>
      <span className="px-1.5 py-0.5 border border-white/20 rounded font-medium">{ageRating}</span>
      <span className="px-1.5 py-0.5 border border-white/20 rounded font-medium">4K</span>
      <span className="px-1.5 py-0.5 border border-white/20 rounded font-medium">9:16</span>
      <span className="font-medium">CC</span>
      <span className="font-medium">AD</span>
      {hasAudio && <Volume2 className="w-3.5 h-3.5" />}
    </div>
  );

  // Share button component
  const ShareButton = ({ className = "" }: { className?: string }) => {
    const handleShare = async () => {
      const shareUrl = `${window.location.origin}/book/${book.id}`;
      const shareTitle = book.title;
      const shareImage = getCacheBustedCoverUrl(book.cover_image_url);

      if (navigator.share) {
        try {
          // Try to share with image if supported
          if (shareImage && navigator.canShare) {
            const response = await fetch(shareImage);
            const blob = await response.blob();
            // Use book title as filename for better display in apps
            const safeTitle = book.title.replace(/[^a-zA-Z0-9\s]/g, '').trim();
            const file = new File([blob], `${safeTitle}.jpg`, { type: blob.type });
            
            // When sharing with files, include URL in text (WhatsApp ignores url field with files)
            const shareData = {
              title: shareTitle,
              text: `Check out "${book.title}" on Buxbox\n${shareUrl}`,
              files: [file],
            };
            if (navigator.canShare(shareData)) {
              await navigator.share(shareData);
              return;
            }
          }
          // Fallback to sharing without image
          await navigator.share({
            title: shareTitle,
            text: `Check out "${book.title}" on Buxbox`,
            url: shareUrl,
          });
        } catch (err) {
          if ((err as Error).name !== 'AbortError') {
            console.error('Share failed:', err);
          }
        }
      } else {
        // Fallback: copy link to clipboard
        try {
          await navigator.clipboard.writeText(shareUrl);
        } catch (err) {
          console.error('Copy failed:', err);
        }
      }
    };

    return (
      <Button
        onClick={handleShare}
        variant="ghost"
        className={cn(
          "h-11 w-11 px-0 transition-all rounded-xl bg-white/10 backdrop-blur-sm text-white border border-white/20 hover:bg-white/20",
          className
        )}
        aria-label="Share"
      >
        <PiShareFatFill className="w-5 h-5" />
      </Button>
    );
  };

  return (
    <>
      <Drawer open={internalOpen} onOpenChange={(open) => { if (!open) handleRequestClose(); }} dismissible={!isCommentsOpen}>
        <DrawerContent 
          className="w-full overflow-hidden z-[12000] !bg-background border-0 p-0" 
          style={{ height: isCommentsOpen ? '100lvh' : '100svh' }}
        >
          <DrawerTitle className="sr-only">{book.title}</DrawerTitle>

          {/* Scrollable Content */}
          <div ref={scrollContainerRef} className="relative w-full h-full overflow-y-auto overscroll-none bg-background">
            
            {/* Hero Section - Fixed height container */}
            <div className="relative h-[75vh] lg:h-[80vh] overflow-hidden">
              <CloseButton onClose={handleRequestClose} />
              
              {/* Background Cover Image - Always visible as base layer */}
              <motion.div
                className="absolute inset-0"
                initial={{ scale: 1.05, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
              >
                <img 
                  src={getCacheBustedCoverUrl(book.cover_image_url)} 
                  alt={book.title}
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </motion.div>

              {/* Random Page Image - Crossfades in on top when loaded */}
              {randomPageImage && (
                <motion.div
                  className="absolute inset-0"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: pageImageLoaded ? 1 : 0 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                >
                  <img 
                    src={randomPageImage}
                    alt={book.title}
                    className="w-full h-full object-cover"
                    loading="eager"
                    onLoad={() => setPageImageLoaded(true)}
                  />
                </motion.div>
              )}

              {/* Dark gradient overlay */}
              <div className="absolute inset-x-0 bottom-0 h-[70%] bg-gradient-to-t from-background via-background/80 to-transparent pointer-events-none" />

              {/* Transition Overlay */}
              <AnimatePresence>
                {isTransitioning && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="absolute inset-0 z-50 bg-black/60 backdrop-blur-sm"
                  />
                )}
              </AnimatePresence>

              {/* Desktop Logo */}
              <div className="absolute top-6 right-8 z-20 hidden lg:block">
                <img 
                  src="https://hwjejrsktuenskryvsvl.supabase.co/storage/v1/object/public/applogos/wide.png" 
                  alt="Buxbox" 
                  className="h-16 w-auto opacity-80" 
                />
              </div>

              {/* Hero Content - Absolute positioned at bottom */}
              <div className="absolute inset-x-0 bottom-0 z-10 px-5 lg:px-12 pb-4 lg:pb-12">
                
                {/* Spacer to push content down on mobile */}
                <div className="h-16 lg:h-0" />
                
                {/* Mobile/Tablet Logo - Centered */}
                <div className="flex justify-center lg:justify-start mb-6">
                  <motion.img 
                    src="https://hwjejrsktuenskryvsvl.supabase.co/storage/v1/object/public/applogos/wide.png" 
                    alt="Buxbox" 
                    className="h-12 w-auto opacity-80 lg:hidden"
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 0.8, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                  />
                </div>

                {/* Content - Single column */}
                <div className="max-w-4xl">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                  >
                    {/* Series Badge + Title */}
                    {seriesInfo.isPartOfSeries && (
                      <div className="flex items-center gap-2 mb-2 justify-center lg:justify-start">
                        <Badge className="bg-primary/90 text-primary-foreground text-xs px-2 py-0.5">
                          {seriesInfo.seriesTitle} • EP {seriesInfo.currentEpisodeNumber}
                        </Badge>
                      </div>
                    )}
                    <h1 className="font-display font-bold text-white tracking-tight text-4xl lg:text-6xl leading-[0.9] mb-4 text-center lg:text-left">
                      {book.title}
                    </h1>

                    {/* Meta Row */}
                    <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-3 gap-y-1 text-white/60 text-sm mb-4">
                      {!isUpcoming && (
                        <>
                          <div className="flex items-center gap-1.5">
                            <StarRating value={Number(averageRating)} readonly size="sm" />
                            <span className="font-medium text-white/80">{averageRating}</span>
                          </div>
                          <span className="text-white/30">•</span>
                        </>
                      )}
                      <span>{bookYear}</span>
                      <span className="text-white/30">•</span>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        <span>{readingTime}</span>
                      </div>
                      {book.tags?.[0] && (
                        <>
                          <span className="text-white/30">•</span>
                          <span className="capitalize">{book.tags[0]}</span>
                        </>
                      )}
                    </div>

                    {/* Quality Badges */}
                    <QualityBadges className="justify-center lg:justify-start mb-5" />

                    {/* Buttons - Side by side */}
                    <div className="flex flex-row gap-3 w-full max-w-md mx-auto lg:mx-0">
                      {!session ? (
                        <>
                          <Button 
                            onClick={() => openAuthModal(`Sign in to read "${book.title}"`)} 
                            className="h-11 flex-1 bg-white text-black hover:bg-white/90 font-semibold rounded-xl text-sm"
                          >
                            <Play className="w-4 h-4 mr-2 fill-current" />
                            Sign In
                          </Button>
                          <AddToListButton />
                          <ShareButton />
                        </>
                      ) : hasAccess ? (
                        <>
                          <ReadNowButton />
                          <AddToListButton />
                          <ShareButton />
                        </>
                      ) : appConfig?.current_model === 'subscription' ? (
                        <>
                          <Button 
                            onClick={() => window.dispatchEvent(new CustomEvent('open-tier-selector', { detail: { onSelect: () => {} } }))}
                            className="h-11 flex-1 bg-white text-black hover:bg-white/90 font-semibold rounded-xl text-sm"
                          >
                            <Play className="w-4 h-4 mr-2 fill-current" />
                            Subscribe
                          </Button>
                          <AddToListButton />
                          <ShareButton />
                        </>
                      ) : appConfig?.current_model === 'bundle' ? (
                        <>
                          <Button 
                            onClick={onPurchase} 
                            className="h-11 flex-1 bg-white text-black hover:bg-white/90 font-semibold rounded-xl text-sm"
                          >
                            Get Bundle
                          </Button>
                          {availability?.canPurchaseIndividually && (
                            <Button
                              onClick={() => setShowIndividualPurchase(true)}
                              className="h-11 flex-1 bg-white/10 backdrop-blur-sm text-white border border-white/20 hover:bg-white/20 rounded-xl text-sm"
                            >
                              Buy Book
                            </Button>
                          )}
                          <AddToListButton iconOnly />
                          <ShareButton />
                        </>
                      ) : (
                        <div className="h-11 flex-1 bg-white/10 rounded-xl animate-pulse" />
                      )}
                    </div>

                  </motion.div>
                </div>

                {/* Cast Row - Full width */}
                <div className="mt-3 max-w-4xl">
                  <BookCastRow bookId={Number(book.id)} bookTitle={book.title} />
                </div>

                {/* Comment Button - Below cast */}
                {!isUpcoming && (
                  <div className="mt-2 flex justify-center lg:justify-start max-w-4xl">
                    <CommentButton 
                      commentCount={reviewCount} 
                      onClick={() => setIsCommentsOpen(true)} 
                    />
                  </div>
                )}
              </div>
            </div>

            {/* About Section - Solid Background */}
            <div className="bg-background px-5 lg:px-12 py-2">
              <div className="max-w-4xl">

                {/* Description */}
                <div>
                  <h3 className="text-white/50 text-xs uppercase tracking-wider mb-2">About</h3>
                  <p className="text-gray-300 text-base leading-relaxed font-apple">
                    {aboutText ? (
                      <>
                        {!isDescriptionExpanded && aboutText.length > 200
                          ? aboutText.slice(0, 220) + '...'
                          : aboutText}
                        {aboutText.length > 200 && (
                          <button
                            onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                            className="text-white font-medium ml-1 hover:text-white/80 transition-colors"
                          >
                            {isDescriptionExpanded ? 'Less' : 'More'}
                          </button>
                        )}
                      </>
                    ) : (
                      <span className="text-white/40 italic">No description available</span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Episodes Section (for series) OR Related Books Carousel */}
            {seriesInfo.isPartOfSeries ? (
              <div className="bg-background px-5 lg:px-12 py-8 border-t border-white/5">
                <div className="mb-5">
                  <h2 className="text-xl lg:text-2xl font-display font-bold text-white/90">
                    Episodes
                    <span className="text-white/50 font-normal text-base ml-2">
                      {seriesInfo.totalEpisodes} episodes
                    </span>
                  </h2>
                </div>
                
                <div 
                  className="overflow-x-auto pb-4 -mx-5 px-5 lg:-mx-12 lg:px-12"
                  style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
                >
                  <div className="flex gap-3 w-max">
                    {seriesInfo.episodes.map(episode => {
                      const isCurrentEpisode = episode.id === book.id;
                      // Use page image if available, fallback to cover
                      const imageUrl = episode.firstPageImage || episode.cover_image_url || '/placeholder-image.png';
                      
                      return (
                        <motion.div 
                          key={episode.id} 
                          className="flex-shrink-0 w-52 lg:w-64 cursor-pointer"
                          whileTap={{ scale: 0.97 }}
                          whileHover={{ scale: 1.02 }}
                          onClick={() => onBookClick?.(episode)}
                        >
                          <div className="relative aspect-video bg-white/5 rounded-lg mb-2 overflow-hidden ring-1 ring-white/10">
                            {/* Episode Badge */}
                            <Badge className="absolute top-2 left-2 z-10 text-[10px] px-1.5 bg-black/70 backdrop-blur-sm text-white">
                              EP {episode.episode_number}
                            </Badge>
                            
                            {/* Watched checkmark */}
                            {episode.isWatched && (
                              <div className="absolute top-2 right-2 z-10 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                                <Check className="w-3 h-3 text-white" />
                              </div>
                            )}
                            
                            <img 
                              src={imageUrl} 
                              alt={episode.title}
                              className="w-full h-full object-cover" 
                            />
                            
                            {/* Progress bar at bottom */}
                            {episode.progress > 0 && (
                              <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/50">
                                <div 
                                  className="h-full bg-primary"
                                  style={{ width: `${episode.progress}%` }}
                                />
                              </div>
                            )}
                          </div>
                          <h3 className="text-white/90 font-medium text-sm leading-tight line-clamp-1">{episode.title}</h3>
                          <p className="text-white/50 text-xs mt-0.5">
                            {episode.isWatched ? 'Watched' : episode.progress > 0 ? `${episode.progress}% complete` : episode.author}
                          </p>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (relatedBooks.data?.length || relatedBooks.isLoading) && (
              <div className="bg-background px-5 lg:px-12 py-8 border-t border-white/5">
                <div className="mb-5">
                  <h2 className="text-xl lg:text-2xl font-display font-bold text-white/90">We think you'll like</h2>
                </div>
                
                <div 
                  className="overflow-x-auto pb-4 -mx-5 px-5 lg:-mx-12 lg:px-12"
                  style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
                >
                  <div className="flex gap-4 w-max">
                    {relatedBooks.isLoading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="flex-shrink-0 w-36 lg:w-44">
                          <div className="aspect-[3/4] bg-white/10 rounded-lg mb-2 animate-pulse" />
                          <div className="h-4 bg-white/10 rounded mb-1 animate-pulse" />
                          <div className="h-3 bg-white/10 rounded w-2/3 animate-pulse" />
                        </div>
                      ))
                    ) : (
                      relatedBooks.data?.map(relatedBook => (
                        <motion.div 
                          key={relatedBook.id} 
                          className="flex-shrink-0 w-36 lg:w-44 cursor-pointer"
                          whileTap={{ scale: 0.97 }}
                          whileHover={{ scale: 1.02 }}
                          onClick={() => onBookClick?.(relatedBook as Book)}
                        >
                          <div className="relative aspect-[3/4] bg-white/5 rounded-lg mb-2 overflow-hidden ring-1 ring-white/10">
                            <img 
                              src={relatedBook.cover_image_url || '/placeholder-image.png'} 
                              alt={relatedBook.title}
                              className="w-full h-full object-cover" 
                            />
                          </div>
                          <h3 className="text-white/90 font-medium text-sm leading-tight line-clamp-2">{relatedBook.title}</h3>
                          <p className="text-white/50 text-xs mt-0.5">{relatedBook.author}</p>
                        </motion.div>
                      ))
                    )}
                  </div>
                </div>

                {!session && (
                  <div className="mt-6 text-center">
                    <p className="text-white/50 text-sm mb-3">Sign in to access these books and more</p>
                    <Button 
                      onClick={() => openAuthModal('Sign in to explore more books')} 
                      className="h-10 px-6 bg-white/10 text-white border border-white/20 hover:bg-white/20 rounded-lg"
                    >
                      Sign In to Explore
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>
      
      {availability?.canPurchaseIndividually && (
        <IndividualBookPurchase 
          book={book} 
          isOpen={showIndividualPurchase} 
          onClose={() => setShowIndividualPurchase(false)} 
          onPurchaseComplete={() => setShowIndividualPurchase(false)} 
          individualPrice={availability.individualPrice || 3.99} 
        />
      )}
      
      <AuthModal isOpen={isAuthModalOpen} onClose={closeAuthModal} context={authContext} />
      
      {bookId && (
        <CommentsBottomSheet 
          bookId={bookId} 
          isOpen={isCommentsOpen} 
          onClose={() => setIsCommentsOpen(false)} 
        />
      )}
    </>
  );
}
